import { useExport } from '@qualio/export-components';
import {
  QButton,
  QMenuButton,
  QMenuItem,
  useCurrentUser,
} from '@qualio/ui-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { exportServiceClient } from '../../../../../api';
import { buildExportDocumentsPayload } from '../../../../../api/exportService';
import { PageOrientation } from '../../../../../api/model/document';
import { hasPermissions } from '../../../../../util/CurrentUser';
import { ButtonGroupContext } from '../../../Contexts/ButtonGroupContext';
import { ExportWrapper } from '../../../Export/ExportWrapper';
import { DocumentOverviewContext } from '../../Context';
import { shouldRenderExportButton } from '../../RenderLogic';

export const EXPORT_BUTTON_LABEL = 'Export';

export const ExportDocumentButton = () => {
  const { t } = useTranslation();
  const { qualioDocument } = useContext(DocumentOverviewContext);
  const { isDisabled, setDisabled } = useContext(ButtonGroupContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeExportMethod, setActiveExportMethod] = useState<
    'legacy' | 'fes' | undefined
  >(undefined);
  const currentUser = useCurrentUser();
  const location = useLocation();
  const { usePagination } = useFlags();

  const { trigger, isLoading } = useExport(exportServiceClient, {
    toastTitle: `Exporting ${qualioDocument.code} ${qualioDocument.title}`,
    'data-cy': 'document-export-toast',
  });

  const handleOpenModal = (exportMethod?: 'legacy' | 'fes') => {
    setActiveExportMethod(exportMethod);
    setIsModalOpen(true);
    setDisabled(true);
  };

  const handleExport = useCallback(
    (
      coverPageEnabled: boolean,
      controlledCopy: boolean,
      contentShrinkingEnabled: boolean,
      includeChangeControl: boolean,
      includeDocx: boolean,
      orientation: PageOrientation = PageOrientation.Portrait,
    ) => {
      const document = qualioDocument;
      trigger(
        buildExportDocumentsPayload([document], currentUser.companyId, false, {
          coverPageEnabled,
          controlledStatus: controlledCopy ? 'controlled' : 'uncontrolled',
          shouldShowShrinkingOption: contentShrinkingEnabled,
          contentShrinkingEnabled,
          includeChangeControl,
          pageOrientation: orientation,
          enablePaginationExportStyles:
            usePagination && document.editor_version?.includes('multiroot'),
          includeDocx,
        }),
      );
    },
    [currentUser.companyId, qualioDocument, trigger, usePagination],
  );

  const isStaff = hasPermissions(currentUser, ['can_manage_app']);

  if (!shouldRenderExportButton(location)) {
    return null;
  }

  const exportWrapper = isModalOpen ? (
    <ExportWrapper
      qualioDocument={qualioDocument}
      isDocumentExportModalOpen={isModalOpen}
      onClose={() => setDisabled(false, setIsModalOpen)}
      onExport={activeExportMethod === 'legacy' ? undefined : handleExport}
      activeExportMethod={activeExportMethod}
    />
  ) : null;

  if (isStaff) {
    return (
      <>
        <QMenuButton
          buttonLabel={t('buttons.export')}
          isDisabled={isDisabled || isLoading}
          variant="ghost"
          data-cy="document-export-button"
          itemSize="sm"
        >
          <QMenuItem
            onClick={() => handleOpenModal('fes')}
            data-cy="export-new"
            fontSize="sm"
          >
            {t('buttons.newExport')}
          </QMenuItem>
          <QMenuItem
            onClick={() => handleOpenModal('legacy')}
            data-cy="export-legacy"
            fontSize="sm"
          >
            {t('buttons.legacyExport')}
          </QMenuItem>
        </QMenuButton>
        {exportWrapper}
      </>
    );
  }

  return (
    <>
      <QButton
        isDisabled={isDisabled || isLoading}
        variant="ghost"
        onClick={() => handleOpenModal()}
        data-cy="document-export-button"
      >
        {t('buttons.export')}
      </QButton>
      {exportWrapper}
    </>
  );
};
